import dayjs from 'dayjs';
import {DEFAULT_HEADER_DATA} from '../components/prosemirror-editor/components/documentHeader/headerFieldOptions';
import {apiMetaToHeaderData} from './documentHeaderMeta';

const AUTHORS_PLACEHOLDER = 'Authors are generated from text attribution.';

/**
 * Format a header field value the same way as preview ReadOnlyField / ClassificationField.
 * @param {*} value
 * @returns {string}
 */
export function formatHeaderFieldDisplay(value) {
    if (Array.isArray(value)) {
        const parts = value
            .map((item) => (typeof item === 'string' ? item : (item?.label || item?.value || '')))
            .filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : '—';
    }
    if (typeof value === 'object' && value !== null) {
        const text = value.label || value.value || '';
        return text || '—';
    }
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    return String(value);
}

/**
 * @param {string|null|undefined} dtg
 */
export function formatHeaderDtgDisplay(dtg) {
    if (!dtg) return '—';
    const parsed = dayjs(dtg);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : String(dtg);
}

/**
 * @param {Array<{displayName?: string, name?: string}>} authors
 */
export function formatAuthorsDisplay(authors) {
    if (!authors?.length) {
        return AUTHORS_PLACEHOLDER;
    }
    return authors
        .map((author) => author.displayName || author.name || 'Unknown')
        .filter(Boolean)
        .join(', ');
}

/**
 * Structured sections for preview-aligned header rendering (DOCX, etc.).
 * @param {object} params
 * @param {object} [params.headerData]
 * @param {Array} [params.authors]
 * @param {string} [params.icrNumber]
 * @returns {Array<{type: 'heading'|'field', title?: string, label?: string, value?: string, boldValue?: boolean, sectionEnd?: boolean, icrTitle?: boolean}>}
 */
export function getDocumentHeaderExportSections({headerData, authors = [], icrNumber} = {}) {
    const data = {...DEFAULT_HEADER_DATA, ...(headerData || {})};
    const sections = [];

    if (icrNumber) {
        sections.push({type: 'field', label: 'ICR Number', value: String(icrNumber), icrTitle: true});
    }

    sections.push({type: 'heading', title: 'Security'});
    sections.push({
        type: 'field',
        label: 'Classification',
        value: formatHeaderFieldDisplay(data.classification),
        boldValue: true,
    });
    sections.push({type: 'field', label: 'IFC', value: formatHeaderFieldDisplay(data.ifc)});
    sections.push({type: 'field', label: 'Distribution', value: formatHeaderFieldDisplay(data.distribution)});
    sections.push({
        type: 'field',
        label: 'Compartment',
        value: formatHeaderFieldDisplay(data.compartment),
        sectionEnd: true,
    });

    sections.push({type: 'heading', title: 'Production'});
    sections.push({
        type: 'field',
        label: 'Authors',
        value: formatAuthorsDisplay(authors),
    });
    sections.push({type: 'field', label: 'DTG', value: formatHeaderDtgDisplay(data.dtg)});
    sections.push({type: 'field', label: 'Originator', value: formatHeaderFieldDisplay(data.originator)});
    sections.push({
        type: 'field',
        label: 'Production Center',
        value: formatHeaderFieldDisplay(data.productionCenter),
        sectionEnd: true,
    });

    sections.push({type: 'heading', title: 'Assessment'});
    sections.push({type: 'field', label: 'Confidence', value: formatHeaderFieldDisplay(data.confidence)});

    return sections;
}

/**
 * @param {object|null|undefined} meta - Export meta (flat and/or nested API shape)
 */
export function resolveHeaderDataFromExportMeta(meta) {
    if (meta?.documentHeader) {
        return meta.documentHeader;
    }
    return apiMetaToHeaderData(meta);
}
